import { apiGet, apiPost, apiPatch, apiDelete, apiPut } from '../../api/axiosInstance';
import type { BulkTagOperation } from '@/generated/api/models/bulkTagOperation';
import type { BulkTagResult } from '@/generated/api/models/bulkTagResult';
import type { EntityTagListResponse } from '@/generated/api/models/entityTagListResponse';
import type { EntityTagsBatchResponse } from '@/generated/api/models/entityTagsBatchResponse';
import type { Tag } from '@/generated/api/models/tag';
import type { TagCreate } from '@/generated/api/models/tagCreate';
import type { TagEntitiesResponse } from '@/generated/api/models/tagEntitiesResponse';
import type { TagListResponse } from '@/generated/api/models/tagListResponse';
import type { TagSuggestionsResponse } from '@/generated/api/models/tagSuggestionsResponse';
import type { TagSummary } from '@/generated/api/models/tagSummary';
import type { TagUpdate } from '@/generated/api/models/tagUpdate';

const BASE = '/api/v1/tags';

export const tagsApi = {
  suggest: (params: { prefix?: string; entity_type?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params.prefix) qs.set('prefix', params.prefix);
    if (params.entity_type) qs.set('entity_type', params.entity_type);
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiGet<TagSuggestionsResponse>(`${BASE}${suffix}`);
  },

  /** Admin-only: every tag in the tenant with usage counts (governance list). */
  listAll: () => apiGet<TagListResponse>(`${BASE}/list`),

  create: (payload: TagCreate) => apiPost<Tag>(BASE, payload),

  /**
   * Apply or remove one tag across many entities of the same type in a
   * single batched request — backs the list-page bulk action.
   */
  bulkTag: (payload: BulkTagOperation) =>
    apiPost<BulkTagResult>(`${BASE}/bulk`, payload),

  /**
   * Resolve tags for many entities (possibly of mixed types) in one request.
   * Each ref is `{module}.{entity}:{uuid}`. Backs the AI-chat entity chips.
   */
  batchForEntities: (refs: string[]) =>
    apiGet<EntityTagsBatchResponse>(
      `${BASE}/entity/batch?${new URLSearchParams({ refs: refs.join(',') })}`,
    ),

  /** Full tag detail (incl. description + usage count) for one tag. */
  get: (id: string) => apiGet<Tag>(`${BASE}/${id}`),

  // Batch-hydrate tag summaries from a list of ids in a single request.
  getByIds: (ids: string[]) =>
    apiGet<TagSummary[]>(
      `${BASE}/by-ids?${new URLSearchParams({ ids: ids.join(',') })}`,
    ),

  update: (id: string, payload: TagUpdate) =>
    apiPatch<Tag>(`${BASE}/${id}`, payload),

  remove: (id: string) => apiDelete(`${BASE}/${id}`),

  merge: (sourceId: string, targetId: string) =>
    apiPost<Tag>(`${BASE}/${sourceId}/merge/${targetId}`, {}),

  /** Admin-only: paginated list of entities a tag is attached to. */
  listEntities: (id: string, params?: { limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.limit !== undefined) qs.set('limit', String(params.limit));
    if (params?.offset !== undefined) qs.set('offset', String(params.offset));
    const suffix = qs.toString() ? `?${qs}` : '';
    return apiGet<TagEntitiesResponse>(`${BASE}/${id}/entities${suffix}`);
  },

  // Entity assignment helpers.
  listForEntity: (entityType: string, entityId: string) =>
    apiGet<EntityTagListResponse>(
      `${BASE}/entity/${encodeURIComponent(entityType)}/${entityId}`,
    ),

  replaceForEntity: (entityType: string, entityId: string, tag_ids: string[]) =>
    apiPut<EntityTagListResponse>(
      `${BASE}/entity/${encodeURIComponent(entityType)}/${entityId}`,
      { tag_ids },
    ),

  assignToEntity: (
    entityType: string,
    entityId: string,
    body: { tag_id?: string; name?: string },
  ) =>
    apiPost<TagSummary>(
      `${BASE}/entity/${encodeURIComponent(entityType)}/${entityId}`,
      body,
    ),

  unassignFromEntity: (entityType: string, entityId: string, tagId: string) =>
    apiDelete(
      `${BASE}/entity/${encodeURIComponent(entityType)}/${entityId}/${tagId}`,
    ),
};
